import React from 'react';
import './BulkPicker.scss';

const DEFAULT_COLUMNS = '20px minmax(0, 1.4fr) minmax(0, 1.2fr) 36px';

/**
 * Pick many records at once: the caller's filters, a checkbox per record,
 * "Select all shown" and Add. Shared by Loan Bids (lender contacts) and the
 * mail merge (investors).
 *
 * Records already taken never show, and a tick is dropped from the Add if its
 * record has since been taken, so nothing is added twice. Ticks survive a
 * change of filter or search, so a caller whose records come from the server
 * can gather them from several searches before adding.
 *
 * `classPrefix` adds the caller's own class names beside the shared ones
 * ("LoanBids" gives LoanBids-bulkItem), so its styles and tests keep working.
 * A native list and plain checkboxes, so nothing portals out of a dialog.
 */
export default function BulkPicker({
    items,
    idOf,
    renderItem,
    takenIds,
    isDisabled,
    disabledReason,
    filters,
    loading,
    failed,
    failedText,
    emptyText,
    footnote,
    groupLabel,
    testId,
    classPrefix,
    columns,
    onAdd,
    onCancel,
}) {
    const [selected, setSelected] = React.useState({});

    const cls = (shared, own) => (classPrefix ? shared + ' ' + classPrefix + '-' + own : shared);
    const keyOf = (item) => String(idOf(item));
    const disabled = (item) => Boolean(isDisabled && isDisabled(item));

    const taken = new Set((takenIds || []).map(String));
    const shown = (items || []).filter((item) => !taken.has(keyOf(item)));
    const selectable = shown.filter((item) => !disabled(item));

    const allShownSelected = selectable.length > 0 && selectable.every((item) => selected[keyOf(item)]);

    const toggle = (item) => setSelected((current) => {
        const next = { ...current };
        if (next[keyOf(item)]) delete next[keyOf(item)];
        else next[keyOf(item)] = item;
        return next;
    });

    const toggleAllShown = () => setSelected((current) => {
        const next = { ...current };
        selectable.forEach((item) => {
            if (allShownSelected) delete next[keyOf(item)];
            else next[keyOf(item)] = item;
        });
        return next;
    });

    const chosen = Object.keys(selected)
        .filter((key) => !taken.has(key))
        .map((key) => selected[key]);

    return (
        <div className={cls('BulkPicker', 'bulk')} data-testid={testId}>
            <div className={cls('BulkPicker-filters', 'bulkFilters')}>{filters}</div>
            <label className={cls('BulkPicker-all', 'bulkAll')}>
                <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} disabled={selectable.length === 0} />
                Select all shown ({selectable.length})
            </label>
            <div className={cls('BulkPicker-list', 'bulkList')} role="group" aria-label={groupLabel}>
                {failed ? <div className={cls('BulkPicker-error', 'error')}>{failedText}</div> : null}
                {!failed && loading ? <div className={cls('BulkPicker-muted', 'muted')}>Loading…</div> : null}
                {!failed && !loading && shown.length === 0 ? <div className={cls('BulkPicker-muted', 'muted')}>{emptyText}</div> : null}
                {!failed && !loading ? shown.map((item) => (
                    <label
                        key={keyOf(item)}
                        className={cls('BulkPicker-item', 'bulkItem') + (disabled(item) ? ' is-disabled' : '')}
                        style={{ gridTemplateColumns: columns || DEFAULT_COLUMNS }}
                        // Shown on hover by the stylesheet: a native title waits
                        // a second and may not show over the disabled checkbox.
                        data-reason={disabled(item) ? disabledReason : undefined}
                    >
                        <input
                            type="checkbox"
                            checked={Boolean(selected[keyOf(item)])}
                            disabled={disabled(item)}
                            onChange={() => toggle(item)}
                        />
                        {renderItem(item)}
                    </label>
                )) : null}
            </div>
            {footnote ? <div className={cls('BulkPicker-footnote', 'bulkFootnote')}>{footnote}</div> : null}
            <div className={cls('BulkPicker-actions', 'bulkActions')}>
                <button type="button" className="button-new" onClick={onCancel}>Cancel</button>
                <button
                    type="button"
                    className="button-new positive"
                    disabled={chosen.length === 0}
                    onClick={() => onAdd(chosen)}
                >
                    Add {chosen.length}
                </button>
            </div>
        </div>
    );
}
